import { useState, type CSSProperties } from 'react';
import { destructionMultiplier, finaleLevelCount, finaleWeapons, type FinaleWeaponId } from '../data/finaleGame';
import { nextVoiceSpeed, type AudioPreferences } from '../lib/audioPreferences';

export type FinalePhase = 'select' | 'confirm' | 'ready' | 'levels' | 'boss' | 'boss-defeat' | 'victory' | 'game-over';

interface FinaleGameHudProps {
  score: number;
  elapsedMs: number;
  level: number;
  phase: FinalePhase;
  copyHits: number;
  copyHitsGoal: number;
  bossHealth: number;
  bossMaxHealth: number;
  health: number;
  shield: number;
  cooldownMs: number;
  cooldownDurationMs: number;
  damage: number;
  powerLevel: number;
  weaponId: FinaleWeaponId;
  drawerOpen: boolean;
  godMode: boolean;
  onDrawerToggle: () => void;
  onWeaponChange: (weaponId: FinaleWeaponId) => void;
  onGodToggle: () => void;
  audioPreferences: AudioPreferences;
  onAudioPreferencesChange: (change: Partial<AudioPreferences>) => void;
}

const formatTime = (milliseconds: number) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  return `${Math.floor(totalSeconds / 60).toString().padStart(2, '0')}:${(totalSeconds % 60).toString().padStart(2, '0')}`;
};

export function FinaleGameHud({ score, elapsedMs, level, phase, copyHits, copyHitsGoal, bossHealth, bossMaxHealth, health, shield, cooldownMs, cooldownDurationMs, damage, powerLevel, weaponId, drawerOpen, godMode, onDrawerToggle, onWeaponChange, onGodToggle, audioPreferences, onAudioPreferencesChange }: FinaleGameHudProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const weapon = finaleWeapons.find((candidate) => candidate.id === weaponId) ?? finaleWeapons[0];
  const boss = phase === 'boss' || phase === 'boss-defeat' || phase === 'victory';
  const objectiveMeter = boss ? bossHealth / bossMaxHealth * 100 : copyHits / copyHitsGoal * 100;
  const objectiveName = boss ? `LEVEL 20/${finaleLevelCount}` : `LEVEL ${level}/${finaleLevelCount}`;
  const objectiveText = boss ? 'SHOOT THE CONVERGENCE CORE' : 'FIND THE DUPLICATE // ORIGINALS ARE INVULNERABLE';

  return (
    <aside className="finale-hud" aria-label="Convergence breach combat HUD" onPointerDown={(event) => event.stopPropagation()}>
      <section className="finale-hud__score" aria-live="polite">
        <span>SCORE</span><strong>{score.toString().padStart(7, '0')}</strong>
        <small>{level - 1} COPIES ERASED // {formatTime(elapsedMs)}</small>
      </section>
      <section className="finale-hud__status" aria-live="polite">
        <div><span>{objectiveName}</span><i style={{ '--meter': `${Math.max(0, objectiveMeter)}%` } as CSSProperties} /></div>
        <b>{objectiveText}</b>
        <small>DESTRUCTION x{destructionMultiplier(level).toLocaleString()} // PLASMA LV {powerLevel} // DMG {damage}</small>
        <i className="finale-hud__cooldown" style={{ '--meter': `${cooldownDurationMs > 0 ? Math.max(0, 100 - cooldownMs / cooldownDurationMs * 100) : 100}%` } as CSSProperties} />
        <div className="finale-hud__vitals"><span>HEALTH {health}</span><i style={{ '--meter': `${health}%` } as CSSProperties} /><span>SHIELD {shield}</span></div>
        <small className="finale-hud__god-line">SPACE = BATTLE CRY // WHAT HAPPENS NEXT BELONGS TO THE GAME<button type="button" aria-label="Toggle hidden God Mode" aria-pressed={godMode} onClick={onGodToggle}>.</button></small>
      </section>
      <section className="finale-hud__arsenal">
        <div className="finale-hud__top-actions"><button type="button" aria-expanded={drawerOpen} onClick={onDrawerToggle}><span>ARSENAL</span><strong>{weapon.shortName}</strong></button><button type="button" aria-expanded={settingsOpen} onClick={() => setSettingsOpen((current) => !current)}>AUDIO</button></div>
        {drawerOpen && <div className="finale-hud__drawer" role="menu" aria-label="Choose a weapon">
          {finaleWeapons.map((candidate) => <button key={candidate.id} type="button" role="menuitem" className={candidate.id === weaponId ? 'is-equipped' : ''} onClick={() => onWeaponChange(candidate.id)}><img src={`/assets/attract/finale/weapons/${candidate.id}.webp`} alt="" /><span>{candidate.name}</span><b>{candidate.damage} DMG</b></button>)}
        </div>}
        {settingsOpen && <div className="finale-hud__audio" aria-label="Shooter settings">
          <label>FIGHTER VOICE <b>{Math.round(audioPreferences.shooterVoiceVolume * 100)}%</b><input type="range" min="0" max="1.5" step="0.05" value={audioPreferences.shooterVoiceVolume} onChange={(event) => onAudioPreferencesChange({ shooterVoiceVolume: Number(event.target.value) })} /></label>
          <button type="button" onClick={() => onAudioPreferencesChange({ shooterVoiceSpeed: nextVoiceSpeed(audioPreferences.shooterVoiceSpeed) })}>VOICE SPEED <b>{audioPreferences.shooterVoiceSpeed}×</b></button>
          <button type="button" onClick={() => onAudioPreferencesChange({ shooterGore: audioPreferences.shooterGore >= 1.5 ? 0.5 : audioPreferences.shooterGore + 0.5 })}>GIB DENSITY <b>{audioPreferences.shooterGore}×</b></button>
          <button type="button" aria-pressed={audioPreferences.shooterShake} onClick={() => onAudioPreferencesChange({ shooterShake: !audioPreferences.shooterShake })}>HIT SHAKE <b>{audioPreferences.shooterShake ? 'ON' : 'OFF'}</b></button>
        </div>}
      </section>
    </aside>
  );
}
